import { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { supabase } from '@/integrations/supabase/client';

const cardValues = [
  ['finance', 'retail', 'health', 'tech', 'legal', 'other'],
  ['automation', 'insights', 'customer', 'product'],
  ['startup', 'scaleup', 'mid', 'enterprise'],
  ['asap', 'q', 'strategic'],
];

const colClasses = ['cfg-cards', 'cfg-cards cfg-cards-2', 'cfg-cards cfg-cards-4', 'cfg-cards'];

function getSolutionBody(key: string, bodies: any, scale: string, timeline: string): string {
  const body = bodies[key] || bodies.automation;
  return body
    .replace('{pct}', scale === 'enterprise' ? '80%+' : '60%+')
    .replace('{time}', timeline === 'asap' ? '3-4 weeks' : 'the first quarter')
    .replace('{volume}', scale === 'enterprise' ? '10,000+' : '1,000+')
    .replace('{timeline}', timeline === 'asap' ? 'MVP in 3 weeks' : 'fully production-ready in 6 weeks');
}

const ConfiguratorSection = () => {
  const { t, language } = useTranslation();
  const cfg = t.configurator as any;

  const totalSteps = 6; // 0-3 questions, 4 contact, 5 result
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const selectCard = (stepIdx: number, value: string) => {
    setAnswers(prev => ({ ...prev, [stepIdx]: value }));
  };

  const next = () => {
    if (step === 3) { setStep(4); return; }
    if (step === 4) { handleSubmit(); return; }
    setStep(s => s + 1);
  };

  const prev = () => { if (step > 0) setStep(s => s - 1); };

  const skip = () => {
    setAnswers(prev => ({ ...prev, [step]: 'other' }));
    if (step === 3) setStep(4);
    else setStep(s => s + 1);
  };

  const challengeKey = answers[1] || 'automation';
  const solutionData = cfg.solutions[challengeKey] || cfg.solutions.automation;
  const solutionBody = getSolutionBody(challengeKey, cfg.solutionBodies, answers[2] || 'mid', answers[3] || 'q');

  const handleSubmit = async () => {
    if (!contactName.trim() || !contactEmail.trim()) return;
    setSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('submit-configurator', {
        body: {
          answers: {
            industry: answers[0] || 'other',
            challenge: answers[1] || 'automation',
            teamScale: answers[2] || 'mid',
            timeline: answers[3] || 'q',
          },
          contact: { name: contactName.trim(), email: contactEmail.trim() },
          solution: { title: solutionData.title, body: solutionBody },
          language,
        },
      });
      if (error) throw error;
    } catch (err) {
      console.error('Submit error:', err);
    }

    setSubmitted(true);
    setSubmitting(false);
    setStep(5);
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setContactName('');
    setContactEmail('');
    setSubmitted(false);
  };

  const progressWidth = step >= 5 ? '100%' : ((step + 1) / totalSteps * 100) + '%';

  return (
    <section id="configurator" className="cfg-section">
      <div className="cfg-header-content">
        <div className="section-tag" style={{ color: 'var(--amber)', justifyContent: 'center' }}>
          <span>{cfg.tag}</span>
        </div>
        <h2 className="section-h" style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 18px' }}>
          {cfg.title} <span className="a">{cfg.titleAccent}</span>
        </h2>
        <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
          {cfg.subtitle}
        </p>
      </div>

      <div className="cfg-shell">
        <div className="cfg-progress-bar">
          <div className="cfg-progress-fill" style={{ width: progressWidth }} />
        </div>

        <div className="cfg-header-bar">
          <div className="cfg-step-indicators">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="cfg-step-ind">
                {i > 0 && <div className={`cfg-step-line${i <= step ? ' done' : ''}`} />}
                <div className={`cfg-step-dot${i < step ? ' done' : i === step ? ' active' : ''}`}>
                  {i < step ? '✓' : i === 5 ? '✓' : i + 1}
                </div>
              </div>
            ))}
          </div>
          <div className={`cfg-step-title${step <= 5 ? ' active' : ''}`}>
            {cfg.stepTitles[step] || cfg.stepTitles[5]}
          </div>
        </div>

        <div className="cfg-body">
          {step < 4 ? (
            <div className="cfg-step active" key={step}>
              <h3>{cfg.steps[step].title}</h3>
              <p className="cfg-hint">{cfg.steps[step].hint}</p>
              <div className={colClasses[step]}>
                {cfg.steps[step].cards.map((card: any, ci: number) => (
                  <div
                    key={cardValues[step][ci]}
                    className={`cfg-card${answers[step] === cardValues[step][ci] ? ' sel' : ''}`}
                    onClick={() => selectCard(step, cardValues[step][ci])}
                  >
                    <div className="cfg-card-icon">{card.icon}</div>
                    <div className="cfg-card-title">{card.title}</div>
                    <div className="cfg-card-sub">{card.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : step === 4 ? (
            <div className="cfg-step active" key="contact">
              <h3>{cfg.contactStep.title}</h3>
              <p className="cfg-hint">{cfg.contactStep.hint}</p>
              <div style={{ maxWidth: 420, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '.78rem', fontWeight: 600, color: 'var(--slate)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                    {cfg.contactStep.nameLabel}
                  </label>
                  <input
                    type="text" value={contactName} onChange={e => setContactName(e.target.value)}
                    placeholder={cfg.contactStep.namePlaceholder}
                    style={{
                      width: '100%', padding: '12px 16px', border: '1.5px solid var(--line-slate)',
                      borderRadius: 12, fontSize: '.88rem', fontFamily: 'var(--f-body)',
                      outline: 'none', background: 'var(--white)', color: 'var(--ink)',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '.78rem', fontWeight: 600, color: 'var(--slate)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                    {cfg.contactStep.emailLabel}
                  </label>
                  <input
                    type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)}
                    placeholder={cfg.contactStep.emailPlaceholder}
                    style={{
                      width: '100%', padding: '12px 16px', border: '1.5px solid var(--line-slate)',
                      borderRadius: 12, fontSize: '.88rem', fontFamily: 'var(--f-body)',
                      outline: 'none', background: 'var(--white)', color: 'var(--ink)',
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="cfg-step active">
              {submitted ? (
                <div className="cfg-result show">
                  <div className="cfg-result-icon">✅</div>
                  <h3>{cfg.confirmation.title}</h3>
                  <p>{cfg.confirmation.message}</p>

                  <div style={{ marginTop: 12, marginBottom: 28 }}>
                    <div className="cfg-result-icon" style={{ fontSize: '2.5rem', marginBottom: 12 }}>{solutionData.emoji}</div>
                    <h4 style={{ fontFamily: 'var(--f-display)', fontSize: '1.2rem', fontWeight: 700, color: 'var(--blue)', marginBottom: 8 }}>{solutionData.title}</h4>
                    <p style={{ fontSize: '.88rem', color: 'var(--slate)', lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>{solutionBody}</p>
                    <div className="cfg-result-tags" style={{ marginTop: 16 }}>
                      {solutionData.tags.map((tag: string) => (
                        <span key={tag} className="cfg-result-tag">{tag}</span>
                      ))}
                    </div>
                  </div>

                  <button type="button" className="cfg-skip" onClick={reset} style={{ cursor: 'pointer' }}>
                    {cfg.confirmation.another}
                  </button>
                </div>
              ) : (
                <div className="cfg-result show">
                  <div className="cfg-result-icon">{solutionData.emoji}</div>
                  <h3>{solutionData.title}</h3>
                  <p>{solutionBody}</p>
                  <div className="cfg-result-tags">
                    {solutionData.tags.map((tag: string) => (
                      <span key={tag} className="cfg-result-tag">{tag}</span>
                    ))}
                  </div>
                  <a href="/#contact" className="btn-hero-p" style={{ display: 'inline-block' }}>
                    {cfg.startProject}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {step < 4 && (
          <div className="cfg-footer">
            <button className="cfg-btn cfg-btn-prev" onClick={prev} style={{ visibility: step > 0 ? 'visible' : 'hidden' }}>
              {cfg.back}
            </button>
            <button type="button" className="cfg-skip" onClick={skip}>{cfg.skip}</button>
            <button className="cfg-btn cfg-btn-next" onClick={next} disabled={!answers[step]}>
              {step === 3 ? cfg.seeResult : cfg.continue}
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="cfg-footer">
            <button className="cfg-btn cfg-btn-prev" onClick={prev}>
              {cfg.back}
            </button>
            <div />
            <button
              className="cfg-btn cfg-btn-next"
              onClick={handleSubmit}
              disabled={!contactName.trim() || !contactEmail.trim() || submitting}
            >
              {submitting ? cfg.submitting : cfg.submitButton}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ConfiguratorSection;
